import { Formik, Form as FormikForm, useField } from "formik";
import React, { useMemo, useState } from "react";
import { Alert } from "components/ui/BootstrapCompat";
import AuthPageFrame from "components/AuthPageFrame";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authForgotPasswordVerify } from "services/main/public.service";

type ResetPasswordValues = {
  identifier: string;
  code: string;
  password: string;
  confirmPassword: string;
};

const initialValues: ResetPasswordValues = {
  identifier: "",
  code: "",
  password: "",
  confirmPassword: "",
};

const RECOVERY_CHANNEL = "email";

const getErrorMessage = (error: any) => {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "ເກີດຂໍ້ຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ"
  );
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusMessage, setStatusMessage] = useState("");
  const [statusVariant, setStatusVariant] = useState<"success" | "danger">(
    "success"
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const identifierParam = useMemo(
    () => searchParams.get("identifier") || "",
    [searchParams]
  );

  return (
    <AuthPageFrame backLabel="ກັບໄປ Login" onBack={() => navigate("/login")}>
      <header className="auth-heading">
        <h1>ຣີເຊັດລະຫັດຜ່ານ</h1>
        <p>ປ້ອນ OTP ທີ່ໄດ້ຮັບ ແລ້ວຕັ້ງລະຫັດຜ່ານໃໝ່ສຳລັບບັນຊີຂອງທ່ານ</p>
      </header>

      <Formik
        enableReinitialize
        initialValues={
          identifierParam
            ? {
                ...initialValues,
                identifier: identifierParam || "",
              }
            : initialValues
        }
        validate={(values) => {
          const errors: Record<string, string> = {};
          if (!values.identifier.trim()) {
            errors.identifier = "ກະລຸນາປ້ອນ username ຫຼື email";
          }
          if (!values.code.trim()) {
            errors.code = "ກະລຸນາປ້ອນ OTP";
          }
          if (!values.password) {
            errors.password = "ກະລຸນາປ້ອນລະຫັດຜ່ານໃໝ່";
          }
          if (!values.confirmPassword) {
            errors.confirmPassword = "ກະລຸນາຢືນຢັນລະຫັດຜ່ານໃໝ່";
          }
          if (values.password !== values.confirmPassword) {
            errors.confirmPassword = "ລະຫັດຜ່ານບໍ່ກົງກັນ";
          }
          return errors;
        }}
        validateOnMount
        onSubmit={async (values, { resetForm }) => {
          setIsSubmitting(true);
          setStatusMessage("");
          try {
            await authForgotPasswordVerify({
              identifier: values.identifier.trim(),
              channel: RECOVERY_CHANNEL,
              code: values.code.trim(),
              password: values.password,
              confirmPassword: values.confirmPassword,
            });
            setStatusVariant("success");
            setStatusMessage(
              "ຣີເຊັດລະຫັດຜ່ານສຳເລັດແລ້ວ ກະລຸນາເຂົ້າລະບົບດ້ວຍລະຫັດຜ່ານໃໝ່ຂອງທ່ານ",
            );
            resetForm();
          } catch (error: any) {
            setStatusVariant("danger");
            setStatusMessage(getErrorMessage(error));
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({ isValid }) => (
          <FormikForm className="auth-register-form">
            <div className="auth-field-stack">
              <RecoveryInput
                label="Username ຫຼື Email"
                name="identifier"
                type="text"
                placeholder="streamer01 ຫຼື example@gmail.com"
                disabled={Boolean(identifierParam)}
                autoComplete="username"
              />

              <RecoveryInput
                label="OTP Code"
                name="code"
                type="text"
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
              />

              <RecoveryPasswordInput
                label="ລະຫັດຜ່ານໃໝ່"
                name="password"
                placeholder="ລະຫັດຜ່ານໃໝ່"
                autoComplete="new-password"
              />

              <RecoveryPasswordInput
                label="ຢືນຢັນລະຫັດຜ່ານໃໝ່"
                name="confirmPassword"
                placeholder="ຢືນຢັນລະຫັດຜ່ານໃໝ່"
                autoComplete="new-password"
              />
            </div>

            {statusMessage && (
              <Alert
                variant={statusVariant}
                className="auth-alert mb-0 mt-4 border-0"
              >
                {statusMessage}
              </Alert>
            )}

            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="auth-submit-button"
            >
              {isSubmitting ? "ກຳລັງບັນທຶກ..." : "ຣີເຊັດລະຫັດຜ່ານ"}
            </button>
          </FormikForm>
        )}
      </Formik>
    </AuthPageFrame>
  );
}

type RecoveryInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  name: keyof ResetPasswordValues;
};

function RecoveryInput({
  label,
  name,
  className = "",
  ...props
}: RecoveryInputProps) {
  const [field, meta] = useField(name);
  const showError = Boolean(meta.touched && meta.error);

  return (
    <label className="auth-field" htmlFor={name}>
      <span>{label}</span>
      <input
        {...field}
        {...props}
        id={name}
        name={name}
        aria-invalid={showError}
        className={`auth-control ${showError ? "is-invalid" : ""} ${className}`.trim()}
      />
      {showError && <small>{meta.error}</small>}
    </label>
  );
}

function RecoveryPasswordInput({
  label,
  name,
  placeholder,
  autoComplete,
}: RecoveryInputProps) {
  const [visible, setVisible] = useState(false);
  const [field, meta] = useField(name);
  const showError = Boolean(meta.touched && meta.error);

  return (
    <label className="auth-field" htmlFor={name}>
      <span>{label}</span>
      <div
        className={`auth-password-control ${
          showError ? "is-invalid" : ""
        }`.trim()}
      >
        <input
          {...field}
          id={name}
          name={name}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={showError}
        />
        <button
          type="button"
          onClick={() => setVisible((currentValue) => !currentValue)}
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
      {showError && <small>{meta.error}</small>}
    </label>
  );
}
